import PropTypes from 'prop-types'
import { Box, Divider, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'

import Accordion from '../../../../components/shared/accordion/accordion.component'
import Button from '../../button/button.component'
import {
  BUTTON_SIZE,
  BUTTON_THEME,
  BUTTON_VARIANTS,
} from '../../../../components/shared/button/button.constants'
import { isLastElement } from '@hapana/shared/utils/array.util'
import { getV1Routes, V1_ROUTES } from '@hapana/shared/utils/v1Routes.util'

import AlertSection from '../alert-section/alert-section.component'
import { styles } from './agreements-individual-alert.styles'

const AgreementsIndividualAlert = ({ agreements, v1ClientId }) => {
  const { t } = useTranslation(['checkIn'])
  const hasMultipleAgreements = agreements?.length > 1
  const [isOpen, setIsOpen] = useState(false)

  const handleChange = (value) => {
    setIsOpen(value)
  }

  const v1Url = getV1Routes(
    V1_ROUTES.PROFILE,
    {
      cid: v1ClientId,
    },
    'payments'
  )

  const renderSingleAgreement = () => {
    const agreement = agreements[0]
    return (
      <Accordion
        isOpen={isOpen}
        onChange={handleChange}
        key={agreement.id || 0}
        summaryStyles={{
          ...styles.accordionSummary,
          ...(isOpen && styles.openedSummary),
        }}
        containerStyles={styles.accordionContent}
        content={
          <Box>
            <Box
              sx={{
                ...styles.multipleAgreementWrapper,
                ...(isOpen && styles.openedMultipleAgreementWrapper),
              }}
            >
              <Box sx={styles.flexRow}>
                <Typography typography="header4" color="gray.64">
                  {agreement.name}
                </Typography>
              </Box>
              <Box sx={styles.flexRow}>
                <Typography typography="medium" color="gray.64">
                  {agreement.date}
                </Typography>
              </Box>
              <Box sx={styles.buttonWrapper}>
                <Button
                  variant={BUTTON_VARIANTS.TEXT}
                  size={BUTTON_SIZE.SMALL}
                  theme={BUTTON_THEME.PRIMARY}
                  stroke
                  sx={styles.margin}
                  label={t('checkIn:resendAgreement')}
                  href={v1Url}
                />
              </Box>
            </Box>
          </Box>
        }
        groupedRows
        label={
          <Box sx={styles.flexColumn}>
            {!isOpen && (
              <Box>
                <Typography typography="medium" color="gray.64">
                  {agreement.name}
                  {agreement.date && ` • ${agreement.date}`}
                </Typography>
              </Box>
            )}
          </Box>
        }
      />
    )
  }

  return (
    <AlertSection
      type="agreements"
      title={t('checkIn:pendingAgreements', { count: agreements.length })}
      count={
        hasMultipleAgreements
          ? t('checkIn:totalAgreementsCard', { count: agreements.length })
          : undefined
      }
    >
      <Box sx={styles.accordionWrapper}>
        {!hasMultipleAgreements ? (
          renderSingleAgreement()
        ) : (
          <Accordion
            containerStyles={styles.accordionContent}
            summaryStyles={{
              ...styles.accordionSummary,
              ...(isOpen && styles.openedSummary),
            }}
            isOpen={isOpen}
            onChange={handleChange}
            content={
              <>
                {agreements.map((agreement, index) => {
                  return (
                    <Box
                      key={agreement.id || index}
                      sx={{
                        ...styles.multipleAgreementWrapper,
                        ...(isOpen && index === 0 && styles.openedMultipleAgreementWrapper),
                      }}
                    >
                      <Box sx={styles.flexRow}>
                        <Typography typography="header4" color="gray.80">
                          {agreement.name}
                        </Typography>
                      </Box>
                      <Box sx={styles.flexRow}>
                        <Typography typography="medium" color="gray.64">
                          {agreement.date}
                        </Typography>
                      </Box>
                      <Box sx={styles.buttonWrapper}>
                        <Button
                          variant={BUTTON_VARIANTS.TEXT}
                          size={BUTTON_SIZE.SMALL}
                          theme={BUTTON_THEME.PRIMARY}
                          stroke
                          sx={styles.margin}
                          label={t('checkIn:resendAgreement')}
                          href={v1Url}
                        />
                      </Box>
                      <Box>
                        {isLastElement(agreements, index) ? (
                          <Divider sx={styles.internalDivider} />
                        ) : null}
                      </Box>
                    </Box>
                  )
                })}
              </>
            }
            groupedRows
            label={
              <Box>
                {!isOpen && (
                  <Box>
                    <Typography typography="medium" color="gray.64">
                      {agreements.map((agreement) => agreement.name).join(', ')}
                    </Typography>
                  </Box>
                )}
              </Box>
            }
          />
        )}
      </Box>
    </AlertSection>
  )
}

AgreementsIndividualAlert.propTypes = {
  agreements: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string.isRequired,
      date: PropTypes.string,
    })
  ),
  v1ClientId: PropTypes.number,
}

export default AgreementsIndividualAlert
